import { Component } from "react";
import ReviewsTileComponent from "./ReviewTileComponent";
import SearchBoxComponent from "./SearchBoxComponent";
import AppSearchResults from "./AppSearchResults";
import DropdownComponent from "./DropdownComponent";
import WordCloudComponent from "./WordCloudComponent";
import { debounce } from "lodash";
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from "recharts";

class ReviewsComponent extends Component {
    constructor (props) {
        super(props)
        this.state = {
            isLoaded: false,
            entry: [],
            searchText: "",
            appId: "",
            appName: "",
            searchEnabled: false,
            showError: false,
            storeSelection: "",
            storeSelected: false,            
            averageRating: 0,
            totalCount: 0,
            searchResults: [],
            showSearchResults: false,
            showSearchBox: false,
            chartData1: [], // Data for the reviews chart
            chartData2: [], // Data for the versions chart
            chartData3: [],
            loading: false,
            loadingText: "Searching",
            reviewSummary: {},
            reviewsByVersion: [],
            activeTab: 'original', // Add this line to track active tab
            aiReviews: {}, // Add this line to store AI reviews,
            reviewsReady: false,
            selectStore: true,
            keywords: {}
        }
        this.debouncedSearch = debounce(this.searchAppsByName.bind(this), 500);
    }

    prepareChartData(entries) {
        // Sort the entries by date first
        const sortedEntries = entries
            .filter((entry) => !isNaN(new Date(entry.updated.label))) // Filter valid dates
            .sort((a, b) => new Date(a.updated.label) - new Date(b.updated.label)); // Sort by date in ascending order
    
        const chartData1 = sortedEntries.map((entry) => ({
            date: new Date(entry.updated.label),
            formattedDate: new Date(entry.updated.label).toLocaleDateString("en-US", { month: "short", year: "numeric" }),
            rating: parseFloat(entry["im:rating"].label)
        }));
    
        const chartData2 = sortedEntries.map((entry) => {
            const date = new Date(entry.updated.label);
            return {
                date, // Raw Date object for sorting
                formattedDate: date.toLocaleDateString("en-US", { month: "short", year: "numeric" }), // Format as "MMM YYYY"
                rating: parseFloat(entry["im:rating"].label), // Extract the rating
                version: entry["im:version"]?.label || "Unknown" // Extract the version or fallback to "Unknown"
            };
        });
    
        const chartData3 = [];
        let cumulativeSum = 0;
    
        sortedEntries.forEach((entry, index) => {
            const date = new Date(entry.updated.label);
            const rating = parseFloat(entry["im:rating"].label);
    
            cumulativeSum += rating; // Add the current rating to the cumulative sum
            const progressiveAverage = (cumulativeSum / (index + 1)).toFixed(2); // Calculate the progressive average
    
            chartData3.push({
                date, // Raw Date object for sorting
                formattedDate: date.toLocaleDateString("en-US", { month: "short", year: "numeric" }), // Format as "MMM YYYY"
                rating, // Current rating
                progressiveAverage: parseFloat(progressiveAverage) // Progressive average
            });
        });
    
        this.setState({ chartData1, chartData2, chartData3 });
    }

    componentWillUnmount() {
        // Cancel any pending debounced calls
        this.debouncedSearch.cancel();
    }

    mergeArrays(data, entry) {
        var a = data.concat(entry);
        return a.filter((item, pos) => a.indexOf(item) === pos);
    }

    sortEntriesByDate(entries) {
        if (entries.length === 0) return entries
        entries.sort((a, b) => {
                const date1 = new Date(a.updated.label);
                const date2 = new Date(b.updated.label);
                return (-1 * date1) - (-1 * date2);
            }
        );
        return entries
    }

    // Add this method to handle tab changes
    handleTabChange(tab) {
        this.setState({ activeTab: tab });
    }

    async getStoreReviews(page, appId, store) {
        this.entry = [];
        //const REVIEW_API_BASE_URL = process.env.REACT_APP_REVIEW_API_BASE_URL
        //const REVIEW_PATH = process.env.REACT_APP_REVIEW_API_PATH_URL;
        //const SORT_ORDER = process.env.REACT_APP_REVIEW_SORT_ORDER;
        try {
            const response = await fetch(`https://api.appstats.org/reviews?id=${appId}&store=${store}&page=${page}`);
            const resp = await response.json();
            const result = resp.body;
    
            if (!result.feed) {
                this.setState({ showError: true });
                return null; // Return null if there's an error
            }
    
            if (!result.feed.entry) {
                return null
            }
    
            const entries = this.sortEntriesByDate(result.feed.entry);
            const count = entries.length;
            const rating = this.calculateAverageRating(entries);
    
            return { rating, count, entries }; // Return the desired data
        } catch (error) {
            return null; // Return null in case of an error
        }
    }

    calculateAverageRating(entries) {
        if (!entries || entries.length === 0) {
            return 0; // Return 0 if the entries array is empty or undefined
        }

        var sum = 0;

        entries.forEach(entry => {
            sum += parseFloat(entry["im:rating"].label);
        });
        return parseFloat((sum / entries.length).toFixed(2)); // Calculate the average
    }

    searchAppsByName(name, storeSelection) {
        fetch(`https://api.appstats.org/apps?name=${name}&store=${storeSelection}`)
            .then(res => res.json())
            .then(result => {
                this.setState({
                    searchResults: result.body.results,
                    showSearchResults: true,
                    loading: false
                });
            })
            .catch(error => {
                this.setState({
                    showError: true,
                    errorMessage: 'Error searching for apps. Please try again.'
                });
            });
    }

    groupReviewsByVersion(entries) {
        const reviewsByVersion = {};
    
        entries.forEach((entry) => {
            const version = entry["im:version"]?.label || "Unknown"; // Extract version or fallback to "Unknown"
            if (!reviewsByVersion[version]) {
                reviewsByVersion[version] = []; // Initialize an array for this version
            }
            reviewsByVersion[version].push(entry.content.label); // Add the review to the corresponding version
        });
    
        return reviewsByVersion;
    }

    async handleAppSelected(app) {
        this.setState({
            averageRating: 0,
            totalCount: 0,
            searchEnabled: false,
            showSearchResults: false,
            showSearchBox: false,
            loading: true,
            loadingText: "Working away...",
            reviewsByVersion: [],
            reviewsReady: false,
            appName: app.trackName
        });

        const reviews1 = await this.getStoreReviews(1, app.trackId, this.state.storeSelection);
        const reviews2 =  false //await this.getStoreReviews(2, app.trackId, this.state.storeSelection);

        if (reviews1 && reviews2) {
            const combineEntries = this.mergeArrays(reviews1.entries, reviews2.entries);
            const averageRating = ((reviews1.rating + reviews2.rating) / 2).toFixed(2);

            const reviewsByVersion = this.groupReviewsByVersion(combineEntries); // Group reviews by version

            const results = this.state.searchResults;
            const filteredApp = results.find(filteredApp => filteredApp.trackId === app.trackId);

            if (filteredApp) {
                const { trackName, primaryGenreName } = filteredApp;
                const requestBody = {
                    app_name: trackName,
                    app_category: primaryGenreName,
                    app_review: reviewsByVersion,
                    app_id: app.trackId
                };

                const stringifiedBody = JSON.stringify(requestBody);

                const reviewSummaries = await fetch(`https://api.appstats.org/reviewSummaries`, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json" // Set the content type to JSON
                        },
                        body: stringifiedBody // Convert the request body to a JSON string
                });

                try {
                    if (reviewSummaries.ok) {
                        const responseText = await reviewSummaries.text(); // Parse response as JSON
                        try {
                            // First clean up the response by removing markdown code block syntax and newlines
                            const cleanedText = responseText
                                .replace(/\n```json\n|\n```/g, '')
                                .replace('=', "")
                                .replace(/\\n/g, '');
                                
                            // Parse the JSON
                            const parsedResponse = JSON.parse(cleanedText);
                            
                            // Extract the review summaries array
                            const { reviewSummaryOutput } = JSON.parse(parsedResponse.body);
                            
                            // Convert array to object with version numbers as keys
                            const reviews = {};
                            reviewSummaryOutput.forEach(review => {
                                reviews[review.versionNumber] = review.reviewSummary;
                            });

                            this.setState({
                                aiReviews: reviews,
                                loading: false,
                                reviewsReady: true,
                                averageRating: averageRating,
                                totalCount: reviews1.count + reviews2.count,
                                entry: combineEntries,
                                appId: app.trackId,
                                showSearchResults: false,
                                showSearchBox: false,
                                reviewsByVersion: reviewsByVersion
                            });

                        } catch (error) {
                            this.setState({
                                aiReviews: [],
                                loading: false,
                                reviewsReady: true,
                                averageRating: averageRating,
                                totalCount: reviews1.count + reviews2.count,
                                entry: combineEntries,
                                appId: app.trackId,
                                showSearchResults: false,
                                showSearchBox: false,
                                reviewsByVersion: reviewsByVersion
                            });
                        }
                    } else if (!reviewSummaries.ok) {
                        this.setState({
                            loading: false,
                            reviewsReady: true,
                            averageRating: averageRating,
                            totalCount: reviews1.count + reviews2.count,
                            entry: combineEntries,
                            appId: app.trackId,
                            showSearchResults: false,
                            showSearchBox: false,
                            reviewsByVersion: reviewsByVersion
                        });
                    }
                }
                catch(error) {
                    this.setState({
                        aiReviews: [],
                        loading: false,
                        reviewsReady: true,
                        averageRating: averageRating,
                        totalCount: reviews1.count + reviews2.count,
                        entry: combineEntries,
                        appId: app.trackId,
                        showSearchResults: false,
                        showSearchBox: false,
                        reviewsByVersion: reviewsByVersion
                    })
                }
            }

            this.prepareChartData(combineEntries); // Prepare data for the chart
        } else if (reviews1 && !reviews2) {
            const reviewsByVersion = this.groupReviewsByVersion(reviews1.entries); // Group reviews by version

            const results = this.state.searchResults;
            const filteredApp = results.find(filteredApp => filteredApp.trackId === app.trackId);

            if (filteredApp) {
                const { trackName, primaryGenreName } = filteredApp;
                const requestBody = {
                    app_name: trackName,
                    app_category: primaryGenreName,
                    app_review: reviewsByVersion,
                    app_id: app.trackId
                };

                const stringifiedBody = JSON.stringify(requestBody);

                try {
                    const reviewSummaries = await fetch(`https://api.appstats.org/reviewSummaries`, {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json" // Set the content type to JSON
                            },
                            body: stringifiedBody // Convert the request body to a JSON string
                    });

                if (reviewSummaries.ok) {
                        const responseText = await reviewSummaries.text(); // Parse response as JSON
                        try {
                            // First clean up the response by removing markdown code block syntax and newlines
                            const cleanedText = responseText
                                .replace(/\n```json\n|\n```/g, '')
                                .replace('=', "")
                                .replace(/\\n/g, '')
                                .replace('_to_summarize','');
                                
                                
                            // Parse the JSON
                            const parsedResponse = JSON.parse(cleanedText);
                            
                            // Extract the review summaries array
                            const output = JSON.parse(parsedResponse.body);
                            const reviewSummaryOutput = output.reviewSummaryOutput;
                            const keywords = output.keywords;
                            
                            // Convert array to object with version numbers as keys
                            const reviews = {};
                            reviewSummaryOutput.forEach(review => {
                                reviews[review.versionNumber] = review.reviewSummary;
                            });

                            this.setState({
                                aiReviews: reviews,
                                loading: false,
                                reviewsReady: true,
                                averageRating: reviews1.rating,
                                totalCount: reviews1.count,
                                entry: reviews1.entries,
                                appId: app.trackId,
                                showSearchResults: false,
                                showSearchBox: false,
                                reviewsByVersion: reviewsByVersion,
                                keywords: keywords
                            });

                        } catch (error) {
                            this.setState({
                                aiReviews: [],
                                loading: false,
                                reviewsReady: true,
                                averageRating: reviews1.rating,
                                totalCount: reviews1.count,
                                entry: reviews1.entries,
                                appId: app.trackId,
                                showSearchResults: false,
                                showSearchBox: false,
                                reviewsByVersion: reviewsByVersion
                            });
                        }

                    } else {
                        this.setState({
                            aiReviews: [],
                            loading: false,
                            reviewsReady: true,
                            averageRating: reviews1.rating,
                            totalCount: reviews1.count,
                            entry: reviews1.entries,
                            appId: app.trackId,
                            showSearchResults: false,
                            showSearchBox: false,
                            reviewsByVersion: reviewsByVersion
                        });
                    }
                } catch (error) {
                    this.setState({
                        aiReviews: [],
                        loading: false,
                        reviewsReady: true,
                        averageRating: reviews1.rating,
                        totalCount: reviews1.count,
                        entry: reviews1.entries,
                        appId: app.trackId,
                        showSearchResults: false,
                        showSearchBox: false,
                        reviewsByVersion: reviewsByVersion
                    });
                }
            }

            this.prepareChartData(reviews1.entries); // Prepare data for the chart
        } else if (!reviews1) {
            this.setState({
                showError: true,
                errorMessage: "No reviews returned, please check back later",
                loading: false
            });
        }
    }

    onStoreSelected(value) {
        this.setState({
            storeSelected: true,
            showSearchBox: true,
            searchEnabled: true,
            storeSelection: value,
            averageRating: 0,
            showSearchResults: false,
            storeName:  value,
            searchResults: [],
            entry: [],
            showError: false,
            chartData1: [],
            chartData2: [],
            chartData3: [],
            loading: false,
            reviewsReady: false,
            appName: "",
            selectStore: false,
            keywords: {}
        })
    }

    searchTextEntered(e) {
        const text = e.target.value;
        this.setState({
            searchText: text,
            showError: false,
            showSearchResults: false,
            appId: "",
            averageRating: 0,
            loading: false
        });

        if (text.length > 2) {
            this.debouncedSearch(text, this.state.storeSelection);
            this.setState({
                loadingText: "Searching...",
                loading: true
            });
        }
    }

    render() {
        return(
            <div className="primaryContainer">
                <header className="headerSection">
                    <h1 className="headerTitle">AppStats</h1>
                    <p className="headerSubtitle">Effortless App Sentiment Analysis</p>
                    <p className="headerSubtitle">© 2025 <a href="https://embry.io/about/" target="_blank" rel="noopener noreferrer">Harsh Yogesh Overseer</a>
                    </p>
                </header>
                <div className="reviewComponent">
                        {this.state.selectStore && 
                            <h3 className="appName">
                                Select a storefront to get started
                            </h3>
                        }
                        <DropdownComponent onSelect={this.onStoreSelected.bind(this)}  storeSelected={this.state.storeSelected}>
                        </DropdownComponent>
                            {this.state.showSearchBox &&
                            <div className="searchComponent">
                                <SearchBoxComponent 
                                    searchTextEntered={this.searchTextEntered.bind(this)}
                                />
                            </div>
                            }
                            {this.state.showError && 
                                <p className="searchBoxErrorText">
                                    {this.state.errorMessage || "No reviews returned!"}
                                </p>
                            }
                        {this.state.loading ? (
                            <div className="loadingContainer">
                                <p className="loadingText">{this.state.loadingText}</p>
                            </div>
                        ) : (
                            <>
                            {this.state.showSearchResults && 
                                <AppSearchResults
                                    results={this.state.searchResults}
                                    onAppSelect={this.handleAppSelected.bind(this)}
                                />
                            }
                            {this.state.appName && 
                                <h2 className="appName">
                                    {this.state.appName}
                                </h2>
                            }
                            {!this.state.loading && this.state.averageRating !== 0 &&
                                <h3 className="ratingText">
                                    The average rating is {this.state.averageRating} over the last {this.state.totalCount} reviews.
                                </h3>
                            }
                            <div className="reviewsContainer">

                            {!this.state.loading && this.state.chartData3.length > 0 && (
                                <div className="chartContainer">
                                    <h4 className="chartTitle">Progressive Average Ratings</h4>
                                    <ResponsiveContainer className="chart" width="100%" height={200}>
                                        <LineChart
                                            width={400}
                                            data={this.state.chartData3}
                                            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                                        >
                                            <XAxis dataKey="formattedDate" />
                                            <YAxis domain={[1, 5]} />
                                            <Tooltip />
                                            <Legend />
                                            <Line type="monotone" dataKey="progressiveAverage" stroke="#82ca9d" />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                            {this.state.chartData1.length > 0 && (
                                <div className="chartContainer">
                                    <h4 className="chartTitle">Chronological Reviews</h4>
                                    <ResponsiveContainer className="chart" width="100%" height={200}>
                                        <LineChart
                                            width={400}
                                            height={200}
                                            data={this.state.chartData1}
                                            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                                        >
                                            <XAxis dataKey="formattedDate" />
                                            <YAxis dataKey={"rating"} domain={[1, 5]} />
                                            <Tooltip hide />
                                            <Legend hide />
                                            <Line type="monotone" dataKey="rating" stroke="#8884d8" />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            )}

                            {this.state.chartData2.length > 0 && (
                                <div className="chartContainer">
                                    <h4 className="chartTitle">Progressive App version Reviews</h4>
                                    <ResponsiveContainer className="chart" width="100%" height={200}>
                                        <LineChart
                                            width={400}
                                            height={200}
                                            data={this.state.chartData2}
                                            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                                        >
                                            <XAxis dataKey="version" />
                                            <YAxis dataKey="rating" domain={[1,5]} />
                                            <Tooltip hide />
                                            <Legend hide />
                                            <Line type="monotone" dataKey="rating" stroke="#8884d8" />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                            {
                                this.state.reviewsReady && (
                                    <WordCloudComponent keywords={this.state.keywords} />
                                )
                            }
                            {this.state.reviewsReady && (
                                <div className="reviewsSection">
                                    <div className="reviewsTabs">
                                        <button 
                                            className={`tabButton ${this.state.activeTab === 'original' ? 'active' : ''}`}
                                            onClick={() => this.handleTabChange('original')}
                                        >
                                            Original Reviews
                                        </button>
                                        {Object.keys(this.state.aiReviews).length > 0 && (
                                        <button 
                                            className={`tabButton ${this.state.activeTab === 'ai' ? 'active' : ''}`}
                                            onClick={() => this.handleTabChange('ai')}
                                        >
                                            AI Insights
                                        </button>)};
                                    </div>
                                    
                                    <div className="reviewsTileContainer">
                                        {this.state.activeTab === 'original' ? (
                                            // Original reviews
                                            this.state.entry.map((res) => (
                                                <ReviewsTileComponent
                                                    key={res.content.label + res.author.name.label} 
                                                    res={res} 
                                                />
                                            ))
                                        ) : (
                                            // AI-generated reviews
                                            Object.entries(this.state.aiReviews).map(([version, review]) => (
                                                <div key={version} className="aiReviewTile">
                                                    <h3>Version {version}</h3>
                                                    <p>{review}</p>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                        </>
                    )};
                </div>
            </div>
        )
    }
}

export default ReviewsComponent